import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';

// Header type definitions
export enum MobileHeaderType {
  FULL = 'full',           // Complete header with profile, notifications, tabs
  SIMPLE = 'simple',       // Only back button
  TITLE_ONLY = 'titleOnly' // Only title, no back button
}

interface ConfigurableMobileHeaderProps {
  type: MobileHeaderType;
  title?: string;
  showBackButton?: boolean;
  backPath?: string;
  transparent?: boolean;
  hideOnMobile?: boolean;
  activeTab?: 'enterprises' | 'trending' | 'insights';
  onTabChange?: (tab: 'enterprises' | 'trending' | 'insights') => void;
  onBackClick?: () => void;
}

const HeaderContainer = styled.header<{ $transparent?: boolean; $hideOnMobile?: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${({ theme }) => theme.sizes.header.height};
  background-color: ${({ $transparent }) => $transparent ? 'transparent' : '#1C0838'};
  z-index: ${({ theme }) => theme.zIndex.header};
  padding: 0 16px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $hideOnMobile }) => $hideOnMobile ? 'none' : 'flex'};
    align-items: center;
    justify-content: space-between;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 24px;
    height: 24px;
  }
`;

const HeaderTitle = styled.h1`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img {
    width: 20px;
    height: 20px;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background-color: #FF3B3B;
  border-radius: 50%;
  border: 2px solid #1C0838;
`;

const TabsContainer = styled.div<{ $hideOnMobile?: boolean }>`
  display: none;
  position: fixed;
  top: ${({ theme }) => theme.sizes.header.height};
  left: 0;
  right: 0;
  height: 48px;
  background-color: #1C0838;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  overflow-x: auto;
  white-space: nowrap;
  z-index: ${({ theme }) => theme.zIndex.header - 1};
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $hideOnMobile }) => $hideOnMobile ? 'none' : 'flex'};
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 15px;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  padding: 0 16px;
  height: 100%;
  position: relative;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 16px;
    height: 2px;
    background-color: ${({ $active }) => $active ? '#fff' : 'transparent'};
    transition: background-color 0.2s ease;
  }
`;

const ConfigurableMobileHeader: React.FC<ConfigurableMobileHeaderProps> = ({
  type = MobileHeaderType.FULL,
  title,
  showBackButton = false,
  backPath,
  transparent = false,
  hideOnMobile = false,
  activeTab = 'enterprises',
  onTabChange,
  onBackClick
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1); // Go back in history
    }
  };

  const handleTabClick = (tab: 'enterprises' | 'trending' | 'insights') => {
    if (onTabChange) {
      onTabChange(tab);
    }
    
    // Navigate to the corresponding page
    switch (tab) {
      case 'enterprises':
        navigate('/enterprises');
        break;
      case 'trending':
        navigate('/trending');
        break;
      case 'insights':
        navigate('/insights');
        break;
    }
  };

  // Render different header types
  const renderHeaderContent = () => {
    switch (type) {
      case MobileHeaderType.SIMPLE:
        // Simple header with only back button
        return (
          <HeaderContainer $transparent={transparent} $hideOnMobile={hideOnMobile}>
            <HeaderLeft>
              {showBackButton && (
                <BackButton onClick={handleBackClick}>
                  <img src="/nav-icons/arrow-left.svg" alt="Back" />
                </BackButton>
              )}
            </HeaderLeft>
          </HeaderContainer>
        );
        
      case MobileHeaderType.TITLE_ONLY:
        // Header with only title
        return (
          <HeaderContainer $transparent={transparent} $hideOnMobile={hideOnMobile}>
            {title && (
              <HeaderCenter>
                <HeaderTitle>{title}</HeaderTitle>
              </HeaderCenter>
            )}
          </HeaderContainer>
        );
        
      case MobileHeaderType.FULL:
      default:
        // Full header with profile, notifications, and tabs
        return (
          <>
            <HeaderContainer $transparent={transparent} $hideOnMobile={hideOnMobile}>
              <ProfileSection>
                <ProfileImage src="/images/profile-pic.jpg" alt="Profile" />
                <ProfileName>Chris Burkard</ProfileName>
                <img 
                  src="/nav-icons/down.svg" 
                  alt="Dropdown" 
                  width="16" 
                  height="16"
                />
              </ProfileSection>
              
              <IconButton>
                <img src="/nav-icons/notification.svg" alt="Notifications" />
                <NotificationBadge />
              </IconButton>
            </HeaderContainer>
            
            <TabsContainer $hideOnMobile={hideOnMobile}>
              <Tab 
                $active={activeTab === 'enterprises'} 
                onClick={() => handleTabClick('enterprises')}
              >
                Enterprises
              </Tab>
              <Tab 
                $active={activeTab === 'trending'} 
                onClick={() => handleTabClick('trending')}
              >
                Trending
              </Tab>
              <Tab 
                $active={activeTab === 'insights'} 
                onClick={() => handleTabClick('insights')}
              >
                Insights
              </Tab>
            </TabsContainer>
          </>
        );
    }
  };
  
  return renderHeaderContent();
};

export default ConfigurableMobileHeader;
